import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { Card, Button, Badge } from 'components/ui';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
  ClockIcon as PendingIcon,
  ExclamationCircleIcon
} from "@heroicons/react/24/outline";
import { getFollowUpsByLeadId, createFollowUp } from 'services/followUpService';
import FollowUpScheduleModal from '../LeadActions/FollowUpScheduleModal';
import { hasPermission } from 'utils/permissionsUtils';
import { getLocalDateTime, formatTime, parseDate } from "../../../utils/dateUtils";

const CalendarTab = ({ isMobile, leadData, initialDate, isStudent = false }) => {
  const { id: routeLeadId } = useParams();

  // Get leadId from props (when in modal) or route params (when in standalone page)
  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId || routeLeadId;

  const [currentDate, setCurrentDate] = useState(() => {
    if (initialDate) {
      const parsed = parseDate(initialDate);
      if (parsed) return parsed;
    }
    return new Date();
  });
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  // Format date helpers - use local date to avoid timezone issues
  // Renamed to avoid conflict with potential imports and clarify purpose (returns YYYY-MM-DD)
  const getLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to construct ISO string from date and time parts for centralized utility
  const constructISO = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const timeComponent = timeStr ? (timeStr.length > 5 ? timeStr : timeStr + ':00') : '00:00:00';
    return `${dateStr.split('T')[0]}T${timeComponent}`;
  };

  // Fetch follow-ups for this lead
  const fetchFollowUps = async () => {
    if (!leadId) {
      console.warn('CalendarTab: No leadId available from props or route params');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await getFollowUpsByLeadId(leadId);

      if (result.success) {
        setFollowUps(result.data || []);
      } else {
        // Handle "no follow-ups found" as empty state, not error
        if (result.error &&
          (result.error.includes("not found") ||
            result.statusCode === 404)) {
          setFollowUps([]);
        } else {
          setError(result.error || "Failed to fetch follow-ups");
        }
      }
    } catch (err) {
      console.error("Error fetching follow-ups:", err);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowUps();
  }, [leadId]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setSelectedFollowUp(null);
      }
    };

    if (selectedFollowUp) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [selectedFollowUp]);

  // Handle follow-up click to show/hide popover
  const handleFollowUpClick = (event, followUp) => {
    event.stopPropagation();

    // If clicking the same follow-up, close the popover
    if (selectedFollowUp?.FollowUpId === followUp.FollowUpId) {
      setSelectedFollowUp(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate position - prefer right side, but flip if not enough space
    let left = rect.right + 8;
    let top = rect.top;

    // If popover would go off right edge, show on left side
    if (left + 320 > viewportWidth) {
      left = rect.left - 328;
    }

    // If popover would go off left edge, center it
    if (left < 8) {
      left = Math.max(8, (viewportWidth - 320) / 2);
    }

    // If popover would go off bottom, adjust top
    if (top + 280 > viewportHeight) {
      top = Math.max(8, viewportHeight - 288);
    }

    setPopoverPosition({ top, left });
    setSelectedFollowUp(followUp);
  };

  // Process follow-ups to add local date information
  const processedFollowUps = useMemo(() => {
    return followUps.map(fu => {
      // Construct UTC ISO string manually to pass to centralized getLocalDateTime
      const localDate = getLocalDateTime(constructISO(fu.ScheduledDate, fu.ScheduledTime));
      return {
        ...fu,
        _localDateObj: localDate,
        _localDateStr: getLocalISODate(localDate),
        _localTimeStr: formatTime(constructISO(fu.ScheduledDate, fu.ScheduledTime))
      };
    });
  }, [followUps]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dateStr = getLocalISODate(current);
      // Filter using the calculated local date string
      const dayFollowUps = processedFollowUps.filter(fu => fu._localDateStr === dateStr);

      days.push({
        date: new Date(current),
        dateStr,
        isCurrentMonth: current.getMonth() === month,
        isToday: getLocalISODate(current) === getLocalISODate(new Date()),
        followUps: dayFollowUps
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentDate, processedFollowUps]);

  // Navigation functions
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Handle follow-up scheduling with API call
  const handleScheduleFollowUp = async (followUpData) => {
    try {
      // Call the API to create new follow-up
      const result = await createFollowUp(followUpData);

      if (result.success) {
        // Refresh the calendar
        await fetchFollowUps();
        return true;
      } else {
        throw new Error(result.error || 'Failed to create follow-up');
      }
    } catch (error) {
      console.error('❌ Error scheduling follow-up from CalendarTab:', error);
      return false;
    }
  };

  // Handle successful follow-up creation (for modal close)
  const handleFollowUpCreated = () => {
    setShowScheduleModal(false);
  };

  if (loading) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
        <div className="flex items-center justify-center">
          <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-b-2"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">
            Loading calendar...
          </span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
        <div className="text-red-600 dark:text-red-400 mb-4">
          Error loading calendar: {error}
        </div>
        <Button
          variant="outlined"
          color="primary"
          onClick={fetchFollowUps}
        >
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Calendar Header */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <CalendarDaysIcon className="w-6 h-6 text-blue-600" />
              <h3 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold text-gray-900 dark:text-gray-100`}>
                Follow-ups Calendar
              </h3>
            </div>
            {hasPermission("FUPSCFUP") ? <Button
              color="primary"
              variant="soft"
              size="sm"
              disabled={isStudent}
              className={isStudent ? "opacity-60 cursor-not-allowed" : ""}
              onClick={() => setShowScheduleModal(true)}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Schedule Follow-up
            </Button> : null}
          </div>

          {/* Calendar Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outlined"
              size="sm"
              onClick={goToPrevMonth}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </Button>

            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h4>

            <Button
              variant="outlined"
              size="sm"
              onClick={goToNextMonth}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            {/* Day Headers */}
            <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  className={`
                    h-[120px] p-2 border-r border-b border-gray-100 dark:border-gray-700 last:border-r-0 relative
                    transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 overflow-hidden
                    ${day.isCurrentMonth
                      ? 'bg-white dark:bg-gray-900'
                      : 'bg-gray-50/50 dark:bg-gray-800/30'
                    }
                    ${day.isToday
                      ? 'bg-blue-50 dark:bg-blue-900/10 ring-2 ring-blue-500 ring-inset'
                      : ''
                    }
                  `}
                >
                  {/* Date Number */}
                  <div className={`
                    flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium mb-2
                    ${day.isCurrentMonth
                      ? 'text-gray-900 dark:text-gray-100'
                      : 'text-gray-400 dark:text-gray-600'
                    }
                    ${day.isToday
                      ? 'bg-blue-600 text-white shadow-lg'
                      : ''
                    }
                  `}>
                    {day.date.getDate()}
                  </div>

                  {/* Follow-ups for this day */}
                  <div className="flex-1 overflow-y-auto space-y-1 max-h-[70px]">
                    {day.followUps.map((followUp, fuIndex) => {
                      const statusId = followUp.StatusId || 1;
                      const eventName = followUp.EventName;

                      // Get status styling for calendar cells based on your status table
                      const getCalendarStatusStyle = (statusId) => {
                        switch (statusId) {
                          case 1: // Pending
                            return {
                              bg: 'bg-yellow-100 dark:bg-yellow-900/20',
                              text: 'text-yellow-800 dark:text-yellow-200',
                              border: 'border-yellow-200 dark:border-yellow-800',
                              dot: 'bg-yellow-500',
                              icon: PendingIcon,
                              iconColor: 'text-yellow-600 dark:text-yellow-400'
                            };
                          case 2: // Completed
                            return {
                              bg: 'bg-green-100 dark:bg-green-900/20',
                              text: 'text-green-800 dark:text-green-200',
                              border: 'border-green-200 dark:border-green-800',
                              dot: 'bg-green-500',
                              icon: CheckCircleIcon,
                              iconColor: 'text-green-600 dark:text-green-400'
                            };
                          case 3: // Missed
                            return {
                              bg: 'bg-orange-100 dark:bg-orange-900/20',
                              text: 'text-orange-800 dark:text-orange-200',
                              border: 'border-orange-200 dark:border-orange-800',
                              dot: 'bg-orange-500',
                              icon: ExclamationTriangleIcon,
                              iconColor: 'text-orange-600 dark:text-orange-400'
                            };
                          case 4: // Cancelled
                            return {
                              bg: 'bg-gray-100 dark:bg-gray-800',
                              text: 'text-gray-800 dark:text-gray-200',
                              border: 'border-gray-200 dark:border-gray-700',
                              dot: 'bg-gray-500',
                              icon: NoSymbolIcon,
                              iconColor: 'text-gray-600 dark:text-gray-400'
                            };
                          case 5: // Failed
                            return {
                              bg: 'bg-red-100 dark:bg-red-900/20',
                              text: 'text-red-800 dark:text-red-200',
                              border: 'border-red-200 dark:border-red-800',
                              dot: 'bg-red-500',
                              icon: XCircleIcon,
                              iconColor: 'text-red-600 dark:text-red-400'
                            };
                          case 6: // Overdue
                            return {
                              bg: 'bg-red-100 dark:bg-red-900/20',
                              text: 'text-red-800 dark:text-red-200',
                              border: 'border-red-200 dark:border-red-800',
                              dot: 'bg-red-600',
                              icon: ExclamationCircleIcon,
                              iconColor: 'text-red-600 dark:text-red-400'
                            };
                          default:
                            return {
                              bg: 'bg-blue-100 dark:bg-blue-900/20',
                              text: 'text-blue-800 dark:text-blue-200',
                              border: 'border-blue-200 dark:border-blue-800',
                              dot: 'bg-blue-500',
                              icon: ClockIcon,
                              iconColor: 'text-blue-600 dark:text-blue-400'
                            };
                        }
                      };

                      const style = getCalendarStatusStyle(statusId);

                      return (
                        <div
                          key={fuIndex}
                          className={`
                            text-xs px-2 py-1 rounded-md truncate cursor-pointer
                            transition-all duration-200 hover:shadow-md hover:scale-[1.02]
                            ${style.bg} ${style.text} border ${style.border}
                            ${selectedFollowUp?.FollowUpId === followUp.FollowUpId ? 'ring-2 ring-blue-500 shadow-md' : ''}
                          `}
                          onClick={(e) => handleFollowUpClick(e, followUp)}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1 min-w-0">
                              <ClockIcon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate font-medium">
                                {followUp._localTimeStr || formatTime(constructISO(followUp.ScheduledDate, followUp.ScheduledTime))}
                              </span>
                            </div>
                            <div className={`w-2 h-2 ${style.dot} rounded-full flex-shrink-0`}></div>
                          </div>
                          {followUp.Remarks && (
                            <div className="mt-1 text-xs opacity-75 truncate">
                              {followUp.Remarks}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Day indicator dot for follow-ups */}
                  {day.followUps.length > 0 && (
                    <div className="absolute top-1 right-1">
                      <div className={`
                        w-2 h-2 rounded-full
                        ${(() => {
                          // Show the most important status color based on priority using StatusId from API
                          if (day.followUps.some(fu => fu.StatusId === 5)) return 'bg-red-500'; // Failed
                          if (day.followUps.some(fu => fu.StatusId === 6)) return 'bg-red-600'; // Overdue
                          if (day.followUps.some(fu => fu.StatusId === 3)) return 'bg-orange-500'; // Missed
                          if (day.followUps.some(fu => fu.StatusId === 2)) return 'bg-green-500'; // Completed
                          if (day.followUps.some(fu => fu.StatusId === 4)) return 'bg-gray-500'; // Cancelled
                          return 'bg-yellow-500'; // Pending (StatusId === 1 or default)
                        })()}
                      `}></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Google Calendar Style Popover */}
        {selectedFollowUp && (
          <div
            ref={popoverRef}
            className="fixed z-50 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Popover Header with colored bar */}
            <div className={`h-1.5 ${selectedFollowUp.StatusId === 1 ? 'bg-yellow-500' :
              selectedFollowUp.StatusId === 2 ? 'bg-green-500' :
                selectedFollowUp.StatusId === 3 ? 'bg-orange-500' :
                  selectedFollowUp.StatusId === 4 ? 'bg-gray-500' :
                    selectedFollowUp.StatusId === 5 ? 'bg-red-500' :
                      selectedFollowUp.StatusId === 6 ? 'bg-red-600' : 'bg-blue-500'
              }`} />

            <div className="p-3">
              {/* Close button */}
              <button
                onClick={() => setSelectedFollowUp(null)}
                className="absolute top-2 right-2 p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XCircleIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>

              {/* Title */}
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 pr-6 mb-2.5 leading-tight">
                {selectedFollowUp.Remarks || 'Follow-up Scheduled'}
              </h3>

              {/* Date & Time */}
              <div className="flex items-center gap-2.5 mb-2">
                <div className="flex items-center justify-center w-7 h-7 rounded-md bg-blue-50 dark:bg-blue-900/20">
                  <CalendarDaysIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 leading-tight">
                    {(selectedFollowUp._localDateObj || getLocalDateTime(constructISO(selectedFollowUp.ScheduledDate, selectedFollowUp.ScheduledTime))).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedFollowUp._localTimeStr || formatTime(constructISO(selectedFollowUp.ScheduledDate, selectedFollowUp.ScheduledTime))}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2.5 mb-2">
                <div className={`flex items-center justify-center w-7 h-7 rounded-md ${selectedFollowUp.StatusId === 1 ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  selectedFollowUp.StatusId === 2 ? 'bg-green-50 dark:bg-green-900/20' :
                    selectedFollowUp.StatusId === 3 ? 'bg-orange-50 dark:bg-orange-900/20' :
                      selectedFollowUp.StatusId === 4 ? 'bg-gray-50 dark:bg-gray-800' :
                        selectedFollowUp.StatusId === 5 ? 'bg-red-50 dark:bg-red-900/20' :
                          selectedFollowUp.StatusId === 6 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'
                  }`}>
                  {selectedFollowUp.StatusId === 1 && <PendingIcon className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" />}
                  {selectedFollowUp.StatusId === 2 && <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />}
                  {selectedFollowUp.StatusId === 3 && <ExclamationTriangleIcon className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />}
                  {selectedFollowUp.StatusId === 4 && <NoSymbolIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />}
                  {selectedFollowUp.StatusId === 5 && <XCircleIcon className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />}
                  {selectedFollowUp.StatusId === 6 && <ExclamationCircleIcon className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />}
                  {![1, 2, 3, 4, 5, 6].includes(selectedFollowUp.StatusId) && <ClockIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />}
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">Status</p>
                  <p className={`text-xs font-medium ${selectedFollowUp.StatusId === 1 ? 'text-yellow-600 dark:text-yellow-400' :
                    selectedFollowUp.StatusId === 2 ? 'text-green-600 dark:text-green-400' :
                      selectedFollowUp.StatusId === 3 ? 'text-orange-600 dark:text-orange-400' :
                        selectedFollowUp.StatusId === 4 ? 'text-gray-600 dark:text-gray-400' :
                          selectedFollowUp.StatusId === 5 ? 'text-red-600 dark:text-red-400' :
                            selectedFollowUp.StatusId === 6 ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
                    }`}>
                    {selectedFollowUp.StatusName || 'Pending'}
                    {selectedFollowUp.EventName && ` - ${selectedFollowUp.EventName}`}
                  </p>
                </div>
              </div>

              {/* Assigned To */}
              {selectedFollowUp.AssignedToName && (
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-7 h-7 rounded-md bg-purple-50 dark:bg-purple-900/20">
                    <UserIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">Assigned To</p>
                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      {selectedFollowUp.AssignedToName}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Follow-ups List */}
        {followUps.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Upcoming Follow-ups
              </h4>
              <Badge variant="soft" color="primary">
                {processedFollowUps.length} total
              </Badge>
            </div>

            <div className="space-y-4">
              {processedFollowUps.slice(0, 5).map((followUp, index) => {
                // Get status information from new API structure
                const statusId = followUp.StatusId || 1;
                const statusName = followUp.StatusName || 'Pending';
                const outcomeId = followUp.OutcomeId;
                const eventName = followUp.EventName;

                // Define status configurations based on your status table
                const getStatusConfig = (statusId, statusName) => {
                  switch (statusId) {
                    case 1: // Pending
                      return {
                        name: 'Pending',
                        color: 'warning',
                        bgColor: 'bg-yellow-50 dark:bg-yellow-900/10',
                        borderColor: 'border-yellow-200 dark:border-yellow-800',
                        indicatorColor: 'bg-yellow-500',
                        icon: PendingIcon,
                        iconColor: 'text-yellow-600 dark:text-yellow-400'
                      };
                    case 2: // Completed
                      return {
                        name: 'Completed',
                        color: 'success',
                        bgColor: 'bg-green-50 dark:bg-green-900/10',
                        borderColor: 'border-green-200 dark:border-green-800',
                        indicatorColor: 'bg-green-500',
                        icon: CheckCircleIcon,
                        iconColor: 'text-green-600 dark:text-green-400'
                      };
                    case 3: // Missed
                      return {
                        name: 'Missed',
                        color: 'warning',
                        bgColor: 'bg-orange-50 dark:bg-orange-900/10',
                        borderColor: 'border-orange-200 dark:border-orange-800',
                        indicatorColor: 'bg-orange-500',
                        icon: ExclamationTriangleIcon,
                        iconColor: 'text-orange-600 dark:text-orange-400'
                      };
                    case 4: // Cancelled
                      return {
                        name: 'Cancelled',
                        color: 'neutral',
                        bgColor: 'bg-gray-50 dark:bg-gray-800',
                        borderColor: 'border-gray-200 dark:border-gray-700',
                        indicatorColor: 'bg-gray-500',
                        icon: NoSymbolIcon,
                        iconColor: 'text-gray-600 dark:text-gray-400'
                      };
                    case 5: // Failed
                      return {
                        name: 'Failed',
                        color: 'error',
                        bgColor: 'bg-red-50 dark:bg-red-900/10',
                        borderColor: 'border-red-200 dark:border-red-800',
                        indicatorColor: 'bg-red-500',
                        icon: XCircleIcon,
                        iconColor: 'text-red-600 dark:text-red-400'
                      };
                    case 6: // Overdue
                      return {
                        name: 'Overdue',
                        color: 'error',
                        bgColor: 'bg-red-50 dark:bg-red-900/10',
                        borderColor: 'border-red-200 dark:border-red-800',
                        indicatorColor: 'bg-red-600',
                        icon: ExclamationCircleIcon,
                        iconColor: 'text-red-600 dark:text-red-400'
                      };
                    default:
                      return {
                        name: statusName || 'Unknown',
                        color: 'neutral',
                        bgColor: 'bg-gray-50 dark:bg-gray-800',
                        borderColor: 'border-gray-200 dark:border-gray-700',
                        indicatorColor: 'bg-gray-400',
                        icon: ClockIcon,
                        iconColor: 'text-gray-600 dark:text-gray-400'
                      };
                  }
                };

                const status = getStatusConfig(statusId, statusName);
                const followUpDate = followUp._localDateObj || getLocalDateTime(constructISO(followUp.ScheduledDate, followUp.ScheduledTime));
                const isToday = getLocalISODate(followUpDate) === getLocalISODate(new Date());
                const isPast = followUpDate < new Date().setHours(0, 0, 0, 0);

                return (
                  <div
                    key={index}
                    className={`
                      relative p-4 rounded-xl border transition-all duration-200 hover:shadow-md
                      ${status.bgColor} ${status.borderColor}
                    `}
                  >
                    {/* Status Indicator */}
                    <div className={`
                      absolute left-0 top-0 bottom-0 w-1 rounded-l-xl
                      ${status.indicatorColor}
                    `}></div>

                    <div className="flex items-start justify-between gap-4 ml-3">
                      <div className="flex-1 min-w-0">
                        {/* Follow-up Title */}
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {followUp.Remarks || 'Follow-up scheduled'}
                          </h5>
                          {isToday && (
                            <Badge variant="soft" color="primary" size="sm">
                              Today
                            </Badge>
                          )}
                          {isPast && statusId === 1 && (
                            <Badge variant="soft" color="error" size="sm">
                              Overdue
                            </Badge>
                          )}
                        </div>

                        {/* Follow-up Details */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <ClockIcon className="w-4 h-4" />
                            <span className="font-medium">
                              {followUpDate.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: followUpDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                              })}
                            </span>
                            <span className="text-gray-400">at</span>
                            <span className="font-medium">{followUp._localTimeStr || formatTime(constructISO(followUp.ScheduledDate, followUp.ScheduledTime))}</span>
                          </div>

                          {followUp.AssignedToName && (
                            <div className="flex items-center gap-1">
                              <UserIcon className="w-4 h-4" />
                              <span>{followUp.AssignedToName}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="flex-shrink-0">
                        <Badge variant="soft" color={status.color}>
                          <div className="flex items-center gap-1">
                            <status.icon className={`w-3 h-3 ${status.iconColor}`} />
                            {eventName ? eventName : status.name}
                            {eventName && statusId === 2 && (
                              <span className="ml-1 text-xs opacity-75">
                                (Completed)
                              </span>
                            )}
                          </div>
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}

              {followUps.length > 5 && (
                <div className="text-center py-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    And {processedFollowUps.length - 5} more follow-ups...
                  </p>
                  <Button variant="outlined" size="sm" className="mt-2">
                    View All Follow-ups
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Empty State */}
        {followUps.length === 0 && (
          <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
            <div
              className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} mx-auto mb-4 flex items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/20`}
            >
              <CalendarDaysIcon className={`${isMobile ? "h-6 w-6" : "h-8 w-8"} text-indigo-600 dark:text-indigo-400`} />
            </div>
            <h3
              className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
            >
              No Follow-ups Scheduled
            </h3>
            <p
              className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 text-gray-500 ${isMobile ? "mb-4" : "mb-6"}`}
            >
              Schedule follow-ups to track and manage interactions with this lead
            </p>
            <Button
              variant="soft"
              color="primary"
              disabled={isStudent}
              className={`${isMobile ? "px-4 py-2 text-sm" : ""} ${isStudent ? "opacity-60 cursor-not-allowed" : ""}`}
              onClick={() => setShowScheduleModal(true)}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Schedule First Follow-up
            </Button>
          </Card>
        )}
      </div>

      {/* Schedule Follow-up Modal */}
      <FollowUpScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        leadData={{ id: leadId }}
        onScheduleFollowUp={handleScheduleFollowUp}
        enableLeadSelection={false}
        selectedDate={null}
        editingFollowUp={null}
      />
    </>
  );
};

export default CalendarTab;